import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shell } from '../components/Shell';
import { MailSettings } from '../components/MailSettings';
import { SlaSettings } from '../components/SlaSettings';
import { PortalSettings } from '../components/PortalSettings';
import { ReqSettings } from '../components/ReqSettings';
import { api, ApiError, type AdminConfig } from '../lib/api';
import { useSession } from '../lib/session';
import { relativeTime } from '../lib/format';

// Configuración de la empresa (solo administradores): equipo, grupos de resolución y categorías.

type Tab = 'team' | 'groups' | 'categories' | 'sla' | 'req' | 'portal' | 'mail' | 'demo';

const input = 'h-9 w-full rounded-md border border-[#D6D3CD] bg-white px-2.5 text-sm focus:border-accent focus:outline-none';
const btnPrimary = 'inline-flex h-9 items-center rounded-md bg-accent px-4 text-sm font-semibold text-white hover:bg-accent-dark disabled:opacity-50';
const btnSecondary = 'inline-flex h-9 items-center rounded-md border border-[#D6D3CD] bg-white px-3 text-sm text-[#3F3D39] hover:bg-paper disabled:opacity-50';

function errorText(e: unknown) {
  return e instanceof ApiError ? e.message : 'Algo salió mal. Inténtalo de nuevo.';
}

function Team({ config, onSaved }: { config: AdminConfig; onSaved: () => void }) {
  const { me } = useSession();
  const changeOn = Boolean(me?.modules?.change);
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: (v: { uid: string; teamLead: boolean; serviceManager?: boolean; groupIds: string[] }) =>
      api(`/admin/agents/${v.uid}`, { method: 'PUT', body: JSON.stringify({ teamLead: v.teamLead, serviceManager: Boolean(v.serviceManager), groupIds: v.groupIds }) }),
    onSuccess: () => { setError(''); onSaved(); },
    onError: e => setError(errorText(e))
  });
  const groups = config.groups.filter(g => g.active);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">
        El equipo viene de Veta. Aquí defines a qué grupos pertenece cada quien y quién es <strong className="font-semibold text-ink">líder de servicio</strong>:
        ve los archivos sensibles de todos los tickets, recibe las alertas de SLA vencido de sus grupos y, más adelante, aprueba ajustes del cierre mensual.
      </p>
      {error && <p role="alert" className="rounded-md bg-[#FBEAEA] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}
      <div className="overflow-x-auto rounded-lg border border-line bg-white">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted">
              <th scope="col" className="border-b border-line px-3 py-3 font-semibold">Agente</th>
              <th scope="col" className="border-b border-line px-3 py-3 font-semibold">Rol</th>
              <th scope="col" className="border-b border-line px-3 py-3 font-semibold">Líder de servicio</th>
              {changeOn && <th scope="col" className="border-b border-line px-3 py-3 font-semibold" title="Revisa estimaciones y cotiza requerimientos (Veta Change)">Gestor del servicio</th>}
              <th scope="col" className="border-b border-line px-3 py-3 font-semibold">Grupos</th>
            </tr>
          </thead>
          <tbody>
            {config.agents.map(a => (
              <tr key={a.uid} className="border-b border-[#EFEDE9] align-top last:border-0">
                <td className="px-3 py-3">
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-muted">{a.email}</div>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-[#3F3D39]">{a.deskRoleLabel}</td>
                <td className="px-3 py-3">
                  {a.vetaAdmin ? (
                    <span className="text-xs text-muted" title="Los administradores ya tienen todos los permisos">Incluido como admin</span>
                  ) : (
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={a.teamLead} disabled={save.isPending}
                        onChange={e => save.mutate({ uid: a.uid, teamLead: e.target.checked, serviceManager: Boolean(a.serviceManager), groupIds: a.groupIds })} />
                      <span className="text-xs">{a.teamLead ? 'Sí' : 'No'}</span>
                    </label>
                  )}
                </td>
                {changeOn && (
                  <td className="px-3 py-3">
                    {a.vetaAdmin ? <span className="text-xs text-muted">Incluido como admin</span> : (
                      <label className="inline-flex items-center gap-2">
                        <input type="checkbox" checked={Boolean(a.serviceManager)} disabled={save.isPending}
                          onChange={e => save.mutate({ uid: a.uid, teamLead: a.teamLead, serviceManager: e.target.checked, groupIds: a.groupIds })} />
                        <span className="text-xs">{a.serviceManager ? 'Sí' : 'No'}</span>
                      </label>
                    )}
                  </td>
                )}
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {groups.map(g => {
                      const on = a.groupIds.includes(g.id);
                      return (
                        <button key={g.id} type="button" aria-pressed={on} disabled={save.isPending}
                          onClick={() => save.mutate({
                            uid: a.uid, teamLead: a.teamLead, serviceManager: Boolean(a.serviceManager),
                            groupIds: on ? a.groupIds.filter(x => x !== g.id) : [...a.groupIds, g.id]
                          })}
                          className={`rounded-full border px-2.5 py-1 text-xs ${on
                            ? 'border-accent bg-accent-soft font-semibold text-accent-dark'
                            : 'border-line bg-white text-muted hover:text-ink'}`}>
                          {on ? '✓ ' : ''}{g.name}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted">¿Falta alguien? Los agentes se dan de alta en Veta; aparecen aquí cuando activan su cuenta.</p>
    </div>
  );
}

function Groups({ config, onSaved }: { config: AdminConfig; onSaved: () => void }) {
  const [rows, setRows] = useState(config.groups);
  const [error, setError] = useState('');
  useEffect(() => setRows(config.groups), [config.groups]);
  const save = useMutation({
    mutationFn: () => api('/admin/groups', { method: 'PUT', body: JSON.stringify({ groups: rows.filter(r => r.name.trim()) }) }),
    onSuccess: () => { setError(''); onSaved(); },
    onError: e => setError(errorText(e))
  });
  const leaders = (id: string) => config.agents.filter(a => a.teamLead && a.groupIds.includes(id)).map(a => a.name);
  const members = (id: string) => config.agents.filter(a => a.groupIds.includes(id)).length;
  const dirty = JSON.stringify(rows) !== JSON.stringify(config.groups);

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">
        Un grupo de resolución es el equipo al que se asigna el ticket antes que a una persona. Un grupo no se borra: se desactiva y los tickets que ya lo tienen lo conservan.
      </p>
      {error && <p role="alert" className="rounded-md bg-[#FBEAEA] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}
      <ul className="flex flex-col gap-2">
        {rows.map((g, i) => (
          <li key={g.id || `new-${i}`} className={`flex flex-wrap items-center gap-3 rounded-lg border bg-white p-3 ${g.active ? 'border-line' : 'border-dashed border-line opacity-70'}`}>
            <label className="sr-only" htmlFor={`g-${i}`}>Nombre del grupo</label>
            <input id={`g-${i}`} className={`${input} max-w-xs`} value={g.name} placeholder="Nombre del grupo"
              onChange={e => setRows(r => r.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
            <span className="text-xs text-muted">{g.id ? `${members(g.id)} ${members(g.id) === 1 ? 'agente' : 'agentes'}` : 'nuevo'}</span>
            {g.id && (
              <span className="text-xs text-[#3F3D39]">
                Líder: {leaders(g.id).length ? leaders(g.id).join(', ') : <span className="text-muted">sin líder</span>}
              </span>
            )}
            <label className="ml-auto inline-flex items-center gap-2 text-xs">
              <input type="checkbox" checked={g.active}
                onChange={e => setRows(r => r.map((x, j) => j === i ? { ...x, active: e.target.checked } : x))} />
              Activo
            </label>
          </li>
        ))}
      </ul>
      <div className="flex gap-2">
        <button type="button" className={btnSecondary} onClick={() => setRows(r => [...r, { id: '', name: '', active: true }])}>+ Agregar grupo</button>
        <button type="button" className={btnPrimary} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Guardando…' : 'Guardar grupos'}
        </button>
      </div>
    </div>
  );
}

function Categories({ config, onSaved }: { config: AdminConfig; onSaved: () => void }) {
  const [rows, setRows] = useState(config.categories);
  const [error, setError] = useState('');
  useEffect(() => setRows(config.categories), [config.categories]);
  const save = useMutation({
    mutationFn: () => api('/admin/categories', {
      method: 'PUT', body: JSON.stringify({ categories: rows.filter(r => r.service.trim() && r.module.trim()) })
    }),
    onSuccess: () => { setError(''); onSaved(); },
    onError: e => setError(errorText(e))
  });
  const dirty = JSON.stringify(rows) !== JSON.stringify(config.categories);
  const set = (i: number, patch: Partial<AdminConfig['categories'][number]>) => setRows(r => r.map((x, j) => j === i ? { ...x, ...patch } : x));

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">Dos niveles: servicio y módulo. Se usan para clasificar, buscar y, más adelante, para asignar y medir.</p>
      {error && <p role="alert" className="rounded-md bg-[#FBEAEA] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}
      <div className="overflow-x-auto rounded-lg border border-line bg-white">
        <table className="w-full min-w-[560px] border-collapse text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted">
              <th scope="col" className="border-b border-line px-3 py-2.5 font-semibold">Servicio</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-semibold">Módulo</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-semibold">Activa</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={c.id || `new-${i}`} className={`border-b border-[#EFEDE9] last:border-0 ${c.active ? '' : 'opacity-60'}`}>
                <td className="px-3 py-2"><input aria-label="Servicio" className={input} value={c.service} onChange={e => set(i, { service: e.target.value })} /></td>
                <td className="px-3 py-2"><input aria-label="Módulo" className={input} value={c.module} onChange={e => set(i, { module: e.target.value })} /></td>
                <td className="px-3 py-2"><input aria-label="Activa" type="checkbox" checked={c.active} onChange={e => set(i, { active: e.target.checked })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <button type="button" className={btnSecondary} onClick={() => setRows(r => [...r, { id: '', service: r[r.length - 1]?.service ?? '', module: '', active: true }])}>+ Agregar categoría</button>
        <button type="button" className={btnPrimary} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Guardando…' : 'Guardar categorías'}
        </button>
      </div>
    </div>
  );
}

function Demo({ onSaved }: { onSaved: () => void }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState('');
  const load = useMutation({
    mutationFn: () => api<{ created: string[] }>('/admin/demo-data', { method: 'POST' }),
    onSuccess: r => {
      setResult(`Listo: se crearon ${r.created.length} tickets de ejemplo.`);
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });
      onSaved();
    },
    onError: e => setResult(errorText(e))
  });
  return (
    <div className="flex max-w-2xl flex-col gap-3 rounded-lg border border-line bg-white p-5">
      <h2 className="text-base font-semibold">Datos de demostración</h2>
      <p className="text-sm text-muted">
        Crea 7 tickets de ejemplo (incidentes, solicitudes y requerimientos en distintas etapas, con conversación) usando los
        clientes, iniciativas y consultores que esta empresa Demo ya tiene en Veta. Solo funciona una vez y solo en empresas de demostración.
      </p>
      <div className="flex items-center gap-3">
        <button type="button" className={btnPrimary} disabled={load.isPending} onClick={() => load.mutate()}>
          {load.isPending ? 'Creando…' : 'Cargar tickets de ejemplo'}
        </button>
        {result && <p role="status" className="text-sm">{result} {result.startsWith('Listo') && <Link to="/" className="text-accent underline">Ver bandeja</Link>}</p>}
      </div>
    </div>
  );
}

const ACTION_LABEL: Record<string, string> = {
  'config:groups': 'actualizó los grupos',
  'config:categories': 'actualizó las categorías',
  'config:agent': 'cambió el rol o los grupos de',
  'config:demo': 'cargó los datos de demostración',
  'config:sla': 'actualizó el SLA',
  'config:portal_user': 'cambió un usuario del portal',
  'config:req': 'actualizó los rubros de requerimientos'
};

export default function Settings() {
  const { me } = useSession();
  const queryClient = useQueryClient();
  // Al volver de Google: ?tab=mail&mail=ok|error|cancelado
  const params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState<Tab>(params.get('tab') === 'mail' ? 'mail' : params.get('tab') === 'sla' ? 'sla' : params.get('tab') === 'portal' ? 'portal' : 'team');
  const [flash] = useState(() => {
    const m = params.get('mail');
    if (m === 'ok') return { kind: 'ok' as const, text: `Buzón conectado: ${params.get('address') ?? ''}` };
    if (m === 'error') return { kind: 'error' as const, text: params.get('message') ?? 'No se pudo conectar el buzón' };
    if (m === 'cancelado') return { kind: 'cancelado' as const, text: 'Se canceló la conexión con Google.' };
    return null;
  });
  const query = useQuery({ queryKey: ['admin-config'], queryFn: () => api<AdminConfig>('/admin/config'), enabled: me?.deskRole === 'admin' });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-config'] });
    void queryClient.invalidateQueries({ queryKey: ['meta'] });
  };

  if (me?.deskRole !== 'admin') {
    return <Shell><p className="rounded-lg border border-line bg-white p-6 text-sm">Solo el administrador puede ver la configuración.</p></Shell>;
  }
  const config = query.data;
  const tabs: [Tab, string][] = [['team', 'Equipo'], ['groups', 'Grupos de resolución'], ['categories', 'Categorías'], ['sla', 'SLA'], ...(me?.modules?.change ? [['req', 'Requerimientos'] as [Tab, string]] : []), ['portal', 'Portal del cliente'], ['mail', 'Correo'],
    ...(config?.tenant.demo ? [['demo', 'Demostración'] as [Tab, string]] : [])];

  return (
    <Shell>
      <div className="flex flex-col gap-5">
        <div>
          <h1 className="text-2xl font-semibold">Configuración</h1>
          <p className="mt-1 text-sm text-muted">{config?.tenant.name}</p>
        </div>
        <div role="tablist" aria-label="Secciones" className="flex flex-wrap gap-1 border-b border-line">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${tab === id ? 'border-accent font-semibold text-ink' : 'border-transparent text-muted hover:text-ink'}`}>
              {label}
            </button>
          ))}
        </div>

        {query.isLoading && <p role="status" className="text-sm text-muted">Cargando…</p>}
        {query.isError && <p role="alert" className="text-sm text-[#8F1D1D]">{errorText(query.error)}</p>}
        {config && tab === 'team' && <Team config={config} onSaved={refresh} />}
        {config && tab === 'groups' && <Groups config={config} onSaved={refresh} />}
        {config && tab === 'categories' && <Categories config={config} onSaved={refresh} />}
        {config && tab === 'demo' && <Demo onSaved={refresh} />}
        {tab === 'sla' && <SlaSettings />}
        {tab === 'portal' && <PortalSettings />}
        {tab === 'req' && <ReqSettings />}
        {tab === 'mail' && <MailSettings flash={flash} />}

        {config && config.history.length > 0 && (
          <section className="rounded-lg border border-line bg-white p-4">
            <h2 className="mb-2 text-sm font-semibold">Cambios recientes</h2>
            <ul className="flex flex-col gap-1.5 text-sm">
              {config.history.slice(0, 8).map((h, i) => (
                <li key={i}>
                  <strong className="font-semibold">{h.actor?.name}</strong> {ACTION_LABEL[h.action] ?? 'cambió la configuración'}
                  {h.action === 'config:agent' && <> <span className="text-ink">{h.changes[0]?.field}</span></>}
                  <span className="text-muted"> · {relativeTime(h.at)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Shell>
  );
}
